import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

import { RebuildScoreCard } from "@/components/RebuildScoreCard";

const GEN_BG = { BA30: "bg-ba30", BA40: "bg-ba40", BA50: "bg-ba50", BA60: "bg-ba60" };

export default function Profile() {
  const { user, refresh } = useAuth();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tz, setTz] = useState(user?.timezone || "Australia/Sydney");
  const [savingTz, setSavingTz] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/subscription/status").then(({ data }) => { if (mounted) setSub(data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => { setTz(user?.timezone || "Australia/Sydney"); }, [user?.timezone]);

  const saveTz = async () => {
    setSavingTz(true);
    try {
      await api.post("/profile/timezone", { timezone: tz });
      toast.success("Timezone updated.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingTz(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success("Membership set to cancel at the end of your current period.");
      setSub((s) => ({ ...(s || {}), cancel_at_period_end: true, cancel_at: data.cancel_at, subscription_status: data.status }));
      setConfirmOpen(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/resume");
      toast.success("Welcome back — your membership will continue.");
      setSub((s) => ({ ...(s || {}), cancel_at_period_end: false, cancel_at: null, subscription_status: data.status }));
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!user) return null;
  const badgeBg = GEN_BG[user.badge] || "bg-primary";
  const cancelAt = sub?.cancel_at ? new Date(sub.cancel_at * 1000).toLocaleDateString() : null;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="label-eyebrow">Profile</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">{user.name}.</h1>
        {user.badge && (
          <div className={`mt-3 inline-flex items-center gap-2 ${badgeBg} text-white rounded-md px-3 py-1.5 font-head uppercase tracking-widest text-xs`}>
            {user.badge} · {user.track}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg p-6 space-y-3">
        <Row k="Email" v={user.email} />
        <Row k="Badge" v={user.badge || "—"} />
        <Row k="Track" v={user.track || "—"} />
        <Row k="Role" v={user.role} />
        <Row k="Start date" v={user.start_date ? new Date(user.start_date).toLocaleDateString() : "—"} />
      </div>

      {/* Timezone — controls Rebuild Score day boundaries */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="profile-timezone">
        <div className="label-eyebrow">Timezone</div>
        <p className="mt-2 text-xs text-muted-foreground">Your Rebuild Score uses this to know when your day closes.</p>
        <div className="mt-3 flex gap-2 flex-wrap">
          <select value={tz} onChange={(e) => setTz(e.target.value)} className="rounded-md border border-border bg-background text-sm px-3 py-2" data-testid="tz-select">
            {["Australia/Sydney","Australia/Melbourne","Australia/Brisbane","Australia/Adelaide","Australia/Perth","Pacific/Auckland","Europe/London","America/New_York","America/Los_Angeles","Asia/Singapore","Asia/Dubai","UTC"].map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
          <button onClick={saveTz} disabled={savingTz || tz === (user?.timezone || "Australia/Sydney")} className="btn-primary btn-sm disabled:opacity-60" data-testid="tz-save-btn">
            {savingTz ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Membership */}
      <div className="bg-card border border-border rounded-lg p-6" data-testid="profile-membership">
        <div className="label-eyebrow">Membership</div>
        {sub?.has_subscription ? (
          <>
            <div className="mt-3 flex items-baseline justify-between">
              <div>
                <div className="font-head uppercase text-xl">BA30 Monthly</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">
                  Status · <span className="text-foreground" data-testid="sub-status">{sub.subscription_status || "—"}</span>
                </div>
              </div>
              <div className="wordmark text-2xl">$59<span className="text-sm text-muted-foreground">/mo</span></div>
            </div>
            {sub.cancel_at_period_end ? (
              <div className="mt-4 rounded-md bg-primary/10 border border-primary/30 p-4">
                <div className="text-sm">
                  Your membership will end{cancelAt ? ` on ${cancelAt}` : " at the end of your period"}. You'll keep full access until then.
                </div>
                <button onClick={resume} disabled={busy}
                  className="btn-primary mt-3 disabled:opacity-60" data-testid="sub-resume-btn">
                  {busy ? "Working…" : "Keep my membership"}
                </button>
              </div>
            ) : (
              <div className="mt-4">
                {!confirmOpen ? (
                  <button onClick={() => setConfirmOpen(true)}
                    className="text-sm underline text-muted-foreground hover:text-destructive" data-testid="sub-cancel-open">
                    Cancel membership
                  </button>
                ) : (
                  <div className="rounded-md bg-muted/60 border border-border p-4">
                    <div className="font-head uppercase text-sm">Are you sure?</div>
                    <p className="text-sm text-foreground/75 mt-2">
                      Your rebuild is a <span className="font-semibold">12-week</span> commitment — most members hit their first real change around Week 6. You'll keep access until the end of your paid period.
                    </p>
                    <div className="mt-4 flex gap-2">
                      <button onClick={cancel} disabled={busy}
                        className="rounded-md border border-destructive text-destructive px-4 py-2 text-sm hover:bg-destructive hover:text-destructive-foreground disabled:opacity-60" data-testid="sub-cancel-confirm">
                        {busy ? "Cancelling…" : "Yes, cancel"}
                      </button>
                      <button onClick={() => setConfirmOpen(false)} disabled={busy}
                        className="btn-primary text-sm px-4 py-2" data-testid="sub-cancel-abort">
                        Keep going
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            {sub === null ? "Loading…" : "No active subscription on file."}
          </p>
        )}
      </div>

      <div className="bg-foreground text-background rounded-lg p-6">
        <div className="label-eyebrow !text-background/70">Milestones (auto-awarded)</div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {["7-Day Walker","12/12 Lifts","80% Month","Week 6 BUILT","12-Week BUILT"].map((b) => (
            <div key={b} className="bg-background/10 rounded-md px-3 py-2 font-head uppercase text-xs tracking-wider">{b}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between text-sm"><span className="text-muted-foreground uppercase tracking-widest text-[11px]">{k}</span><span>{v}</span></div>;
}
